import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { RootState, AppDispatch } from '../redux/store';
import {
  setSubtasks,
  setError,
  generateSubtasks,
  updateSubtaskStatus,
} from '../redux/slices/subtasksSlice';
import { Subtask, SubtaskStatus } from '../models/Subtask';

// Helper function to convert Firestore data
const convertFirestoreData = (doc: any): Subtask => {
  const data = doc.data();

  // Convert Timestamp objects to ISO strings
  return {
    id: doc.id,
    ...data,
    createdAt:
      data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt:
      data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  } as Subtask;
};

export const useSubtasks = (problemId: string, userId: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const subtasks = useSelector((state: RootState) => state.subtasks.subtasks);
  const loading = useSelector((state: RootState) => state.subtasks.loading);
  const error = useSelector((state: RootState) => state.subtasks.error);
  const generatingSubtasks = useSelector((state: RootState) => state.subtasks.generatingSubtasks);

  useEffect(() => {
    // Set up real-time listener for subtasks
    const subtasksQuery = query(
      collection(db, 'subtasks'),
      where('problemId', '==', problemId),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(
      subtasksQuery,
      snapshot => {
        const subtasksList: Subtask[] = [];
        snapshot.forEach(doc => {
          // Convert Firestore document to Subtask with serializable dates
          subtasksList.push(convertFirestoreData(doc));
        });
        dispatch(setSubtasks(subtasksList));
      },
      error => {
        console.error('Error fetching subtasks:', error);
        dispatch(setError(error.message));
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [dispatch, problemId, userId]);

  // Function to trigger subtasks generation
  const generateProblemSubtasks = async () => {
    try {
      await dispatch(generateSubtasks({ problemId, userId })).unwrap();
    } catch (error) {
      console.error('Failed to generate subtasks:', error);
    }
  };

  // Function to update subtask status
  const updateStatus = async (subtaskId: string, status: SubtaskStatus) => {
    try {
      await dispatch(updateSubtaskStatus({ subtaskId, status })).unwrap();
    } catch (error) {
      console.error('Failed to update subtask status:', error);
    }
  };

  return {
    subtasks,
    loading,
    error,
    generatingSubtasks,
    generateProblemSubtasks,
    updateStatus,
  };
};
